// Events: things that happen at a time (dentist, conference, flight). Unlike
// tasks they're never to-dos: no checkbox, never overdue, and "done" is derived
// from the end time at read time rather than stored.

export const DAY_MS = 86_400_000
// A timed event with no length said ("dentist at 3pm").
export const EVENT_DEFAULT_MINUTES = 45
// Converting a task to an event.
export const CONVERT_DEFAULT_MINUTES = 30
export const EVENT_DURATION_CHIPS = [15, 30, 45, 60] as const
export const REMIND_OPTIONS = [10, 30, 60, 1440] as const
// All-day events remind relative to 09:00 on the day, not midnight.
const ALL_DAY_REMIND_ANCHOR_MS = 9 * 3_600_000

export type EventTiming = {
  startsAt: number
  allDay?: boolean | null
  durationMinutes?: number | null
}

export function eventEndMs(e: EventTiming): number {
  if (e.allDay) return e.startsAt + DAY_MS
  return e.startsAt + (e.durationMinutes ?? EVENT_DEFAULT_MINUTES) * 60_000
}

export type EventPhase = 'upcoming' | 'now' | 'done'

export function eventPhase(e: EventTiming, now: number): EventPhase {
  if (now >= eventEndMs(e)) return 'done'
  return now >= e.startsAt ? 'now' : 'upcoming'
}

export const isEventDone = (e: EventTiming, now: number) => now >= eventEndMs(e)

export function reminderFireAt(e: EventTiming & { remindMinutesBefore?: number | null }) {
  if (e.remindMinutesBefore == null) return null
  const anchor = e.allDay ? e.startsAt + ALL_DAY_REMIND_ANCHOR_MS : e.startsAt
  return anchor - e.remindMinutesBefore * 60_000
}

// "in 2h", "Now · 22m left", "Done" — clients localize; this picks the case.
export type EventRelative =
  | { kind: 'in'; minutes: number }
  | { kind: 'now'; minutesLeft: number }
  | { kind: 'allDay' }
  | { kind: 'done' }

export function eventRelative(e: EventTiming, now: number): EventRelative {
  const phase = eventPhase(e, now)
  if (phase === 'done') return { kind: 'done' }
  if (e.allDay) return { kind: 'allDay' }
  if (phase === 'now')
    return { kind: 'now', minutesLeft: Math.ceil((eventEndMs(e) - now) / 60_000) }
  return { kind: 'in', minutes: Math.ceil((e.startsAt - now) / 60_000) }
}

export const clampDuration = (m: number) => Math.min(1440, Math.max(5, Math.round(m / 5) * 5))

// Local midnight → start of the local day in a tz (minutes, UTC − local).
export function localDayStartMs(day: string, tzOffsetMin: number) {
  return Date.parse(`${day}T00:00:00Z`) + tzOffsetMin * 60_000
}

export function localDayOf(ms: number, tzOffsetMin: number) {
  return new Date(ms - tzOffsetMin * 60_000).toISOString().slice(0, 10)
}

export function localHHMM(ms: number, tzOffsetMin: number) {
  return new Date(ms - tzOffsetMin * 60_000).toISOString().slice(11, 16)
}

// Merge overlapping busy intervals (mirrored meetings across calendars).
export function mergeIntervals(list: { start: number; end: number }[]) {
  const sorted = [...list].filter((i) => i.end > i.start).sort((a, b) => a.start - b.start)
  const out: { start: number; end: number }[] = []
  for (const i of sorted) {
    const last = out[out.length - 1]
    if (last && i.start <= last.end) last.end = Math.max(last.end, i.end)
    else out.push({ ...i })
  }
  return out
}
